import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { getClassAnalyticsSummary, getClassAnalyticsStudents, getClassAnalyticsMastery, getClassAnalyticsInsights } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Users, TrendingUp, TrendingDown, Minus, AlertTriangle, BookOpen, ChevronRight, Eye, ArrowUpDown, Shield, CircleCheck, CircleAlert, CircleDashed, Info } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { AnalyticsFilterBar } from '../components/AnalyticsFilterBar';

const statusColors = {
  mastered: 'text-emerald-400 bg-emerald-400/10',
  developing: 'text-blue-400 bg-blue-400/10',
  needs_work: 'text-red-400 bg-red-400/10',
  insufficient: 'text-muted-foreground bg-muted/50',
};
const statusLabels = { mastered: 'Maîtrisé', developing: 'En cours', needs_work: 'À travailler', insufficient: 'Données insuff.' };
const reliabilityLabels = { high: 'Élevée', medium: 'Moyenne', low: 'Faible', insufficient: 'Insuffisante' };
const reliabilityColors = { high: 'text-emerald-400', medium: 'text-blue-400', low: 'text-amber-400', insufficient: 'text-muted-foreground' };
const decisionLabels = { review: 'À revoir', consolidate: 'À consolider', move_forward: 'Avancer', reassess: 'À réévaluer' };
const decisionColors = { review: 'text-red-400 bg-red-400/10', consolidate: 'text-amber-400 bg-amber-400/10', move_forward: 'text-emerald-400 bg-emerald-400/10', reassess: 'text-muted-foreground bg-muted/50' };

function ProgressIndicator({ value }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground text-xs">—</span>;
  const color = value >= 5 ? 'text-emerald-400' : value <= -5 ? 'text-red-400' : 'text-muted-foreground';
  const Icon = value >= 5 ? TrendingUp : value <= -5 ? TrendingDown : Minus;
  return <span className={`inline-flex items-center gap-1 text-sm font-semibold ${color}`}><Icon className="w-3.5 h-3.5" />{value > 0 ? '+' : ''}{value}%</span>;
}

function LevelBadge({ value }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground text-xs">—</span>;
  const color = value >= 75 ? 'text-emerald-400' : value >= 50 ? 'text-blue-400' : value >= 30 ? 'text-amber-400' : 'text-red-400';
  return <span className={`text-sm font-bold ${color}`}>{value}%</span>;
}

export default function ClassDashboard() {
  const { classId } = useParams();
  usePageTitle('Suivi classe');

  const [summary, setSummary] = useState(null);
  const [students, setStudents] = useState([]);
  const [mastery, setMastery] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('student_name');
  const [sortDir, setSortDir] = useState('asc');
  const [tab, setTab] = useState('students');
  const [searchParams] = useSearchParams();
  const loadedOnce = useRef(false);

  const load = useCallback(async (f = {}) => {
    setLoading(true);
    try {
      const [sumRes, stuRes, masRes, insRes] = await Promise.all([
        getClassAnalyticsSummary(classId, f),
        getClassAnalyticsStudents(classId, f),
        getClassAnalyticsMastery(classId, f),
        getClassAnalyticsInsights(classId, f),
      ]);
      setSummary(sumRes.data);
      setStudents(stuRes.data.data);
      setMastery(masRes.data.data);
      setInsights(insRes.data.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    }
    setLoading(false);
  }, [classId]);

  // Initial load: read filters from URL params
  useEffect(() => {
    if (loadedOnce.current) return;
    loadedOnce.current = true;
    const init = {};
    for (const k of ['date_from', 'date_to', 'subject_id', 'chapter_id', 'notion_id']) {
      if (searchParams.get(k)) init[k] = searchParams.get(k);
    }
    load(init);
  }, [load, searchParams]);

  const handleFiltersChange = (newFilters) => {
    load(newFilters);
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedStudents = useMemo(() => [...students].sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey];
    if (av === null || av === undefined) av = sortDir === 'asc' ? Infinity : -Infinity;
    if (bv === null || bv === undefined) bv = sortDir === 'asc' ? Infinity : -Infinity;
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  }), [students, sortKey, sortDir]);

  if (!loading && !summary) {
    return (
      <div className="text-center py-24">
        <p className="text-muted-foreground">Classe introuvable ou aucune donnée.</p>
        <Link to="/tracking"><Button variant="secondary" className="mt-4"><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button></Link>
      </div>
    );
  }

  const cards = summary ? [
    {
      id: 'median', label: 'Score médian', value: summary.median_score?.value != null ? `${summary.median_score.value}%` : '—',
      sub: summary.median_score?.students_included ? `${summary.median_score.students_included} élèves` : null,
      color: summary.median_score?.value >= 75 ? 'text-emerald-400' : summary.median_score?.value >= 50 ? 'text-blue-400' : summary.median_score?.value != null ? 'text-amber-400' : 'text-muted-foreground',
    },
    {
      id: 'mastery', label: 'Taux de maîtrise', value: summary.mastery_rate?.value != null ? `${summary.mastery_rate.value}%` : '—',
      sub: summary.mastery_rate?.value != null ? `${summary.mastery_rate.mastered_count}/${summary.mastery_rate.coverage} élèves ≥${summary.mastery_rate.threshold}%` : null,
      color: summary.mastery_rate?.value >= 60 ? 'text-emerald-400' : summary.mastery_rate?.value >= 40 ? 'text-amber-400' : summary.mastery_rate?.value != null ? 'text-red-400' : 'text-muted-foreground',
    },
    {
      id: 'support', label: 'À accompagner', value: summary.students_needing_support || 0,
      sub: 'élèves en difficulté',
      color: summary.students_needing_support > 0 ? 'text-amber-400' : 'text-emerald-400',
    },
    {
      id: 'progress', label: 'Progression', value: summary.class_progress?.median_progress != null ? `${summary.class_progress.median_progress > 0 ? '+' : ''}${summary.class_progress.median_progress}%` : '—',
      sub: summary.class_progress?.students_included ? `${summary.class_progress.students_progressing}↑ ${summary.class_progress.students_stable}→ ${summary.class_progress.students_declining}↓` : null,
      color: summary.class_progress?.median_progress > 0 ? 'text-emerald-400' : summary.class_progress?.median_progress < 0 ? 'text-red-400' : 'text-muted-foreground',
    },
    {
      id: 'fragile', label: 'Notions fragiles', value: summary.fragile_notions_count || 0,
      sub: 'notions < 50%',
      color: summary.fragile_notions_count > 0 ? 'text-red-400' : 'text-emerald-400',
    },
    {
      id: 'participation', label: 'Participation', value: `${summary.participation?.with_attempt || 0}/${summary.participation?.total || 0}`,
      sub: summary.participation?.without_data > 0 ? `${summary.participation.without_data} sans données` : 'Tous évalués',
      color: summary.participation?.without_data > 0 ? 'text-amber-400' : 'text-emerald-400',
    },
  ] : [];

  const SortHeader = ({ k, children }) => (
    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort(k)}>
      <span className="inline-flex items-center gap-1">{children}{sortKey === k && <ArrowUpDown className="w-3 h-3" />}</span>
    </TableHead>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="class-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/tracking">
            <Button variant="ghost" size="sm" data-testid="back-to-tracking"><ArrowLeft className="w-4 h-4" /></Button>
          </Link>
          <div>
            <h1 className="text-3xl font-black tracking-tight" data-testid="class-name">{summary?.class_name || 'Chargement...'}</h1>
            {summary && <p className="text-muted-foreground mt-0.5 text-sm">{summary.active_students} élèves · {summary.quiz_count} QCM · {summary.niveau} {summary.annee}</p>}
          </div>
        </div>
      </div>

      {/* Filters */}
      <AnalyticsFilterBar onFiltersChange={handleFiltersChange} />

      {loading && (
        <div className="flex items-center justify-center py-12" data-testid="class-dashboard-loading">
          <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" /> <span className="text-muted-foreground text-sm">Chargement des données...</span>
        </div>
      )}

      {!loading && summary && (<>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="summary-cards">
        {cards.map(c => (
          <Card key={c.id} className="bg-card border-border" data-testid={`card-${c.id}`}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">{c.label}</p>
              <p className={`text-2xl font-black ${c.color}`}>{c.value}</p>
              {c.sub && <p className="text-xs text-muted-foreground mt-1">{c.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Priority Insights */}
      {insights.length > 0 && (
        <Card className="bg-card border-border border-amber-500/20" data-testid="insights-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Alertes pédagogiques</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {insights.map((ins, i) => (
              <div key={`insight-${ins.title}-${i}`} className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/10" data-testid={`insight-${i}`}>
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium">{ins.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{ins.evidence}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tab switch */}
      <div className="flex gap-1 bg-muted/30 p-1 rounded-lg w-fit" data-testid="tab-switch">
        <button onClick={() => setTab('students')} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'students' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-students">
          <Users className="w-4 h-4 inline mr-1.5" />Élèves ({students.length})
        </button>
        <button onClick={() => setTab('mastery')} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'mastery' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-mastery">
          <BookOpen className="w-4 h-4 inline mr-1.5" />Notions ({mastery.length})
        </button>
      </div>

      {/* Students Table */}
      {tab === 'students' && (
        <Card className="bg-card border-border" data-testid="students-table">
          <CardContent className="p-0">
            {sortedStudents.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <SortHeader k="student_name">Élève</SortHeader>
                    <SortHeader k="last_score">Dernier</SortHeader>
                    <SortHeader k="recent_level">Niveau</SortHeader>
                    <SortHeader k="progress">Progression</SortHeader>
                    <SortHeader k="mastered_count">Maîtrisées</SortHeader>
                    <SortHeader k="needs_work_count">À travailler</SortHeader>
                    <SortHeader k="alerts_count">Alertes</SortHeader>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedStudents.map((s) => (
                    <TableRow key={s.student_id} className="hover:bg-muted/30 transition-colors" data-testid={`student-row-${s.student_id}`}>
                      <TableCell className="font-medium">
                        {s.student_name}
                        {!s.has_data && <span className="ml-2 text-xs text-muted-foreground italic">Aucune donnée</span>}
                      </TableCell>
                      <TableCell><LevelBadge value={s.last_score} /></TableCell>
                      <TableCell>
                        <LevelBadge value={s.recent_level} />
                        {s.recent_level_attempts != null && s.recent_level_attempts < 3 && s.has_data && (
                          <span className="ml-1 text-xs text-muted-foreground">({s.recent_level_attempts}t)</span>
                        )}
                      </TableCell>
                      <TableCell><ProgressIndicator value={s.progress} /></TableCell>
                      <TableCell>
                        {s.mastered_count > 0 ? <span className="text-sm text-emerald-400 font-semibold">{s.mastered_count}</span> : <span className="text-xs text-muted-foreground">0</span>}
                      </TableCell>
                      <TableCell>
                        {s.needs_work_count > 0 ? <span className="text-sm text-red-400 font-semibold">{s.needs_work_count}</span> : <span className="text-xs text-muted-foreground">0</span>}
                      </TableCell>
                      <TableCell>
                        {s.alerts_count > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-400">
                            <AlertTriangle className="w-3 h-3" />{s.alerts_count}
                          </span>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <Link to={`/tracking/${classId}/student/${s.student_id}`}>
                          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary" data-testid={`view-student-${s.student_id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
                <p className="text-muted-foreground">Aucun élève dans cette classe.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Notion Mastery Table */}
      {tab === 'mastery' && (
        <Card className="bg-card border-border" data-testid="mastery-table">
          <CardContent className="p-0">
            {mastery.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Notion</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Taux réussite</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Couverture</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Maîtrisé</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">En cours</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">À travailler</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Fiabilité</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Décision</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mastery.map((m) => (
                    <TableRow key={m.notion_id} className="hover:bg-muted/30 transition-colors" data-testid={`mastery-row-${m.notion_id}`}>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">{m.notion_label}</p>
                          {(m.theme || m.chapter) && <p className="text-xs text-muted-foreground">{[m.theme, m.chapter].filter(Boolean).join(' › ')}</p>}
                        </div>
                      </TableCell>
                      <TableCell><LevelBadge value={m.success_rate} /></TableCell>
                      <TableCell>
                        <span className="text-sm">{m.coverage_count}</span>
                        {m.coverage_rate != null && <span className="text-xs text-muted-foreground ml-1">({m.coverage_rate}%)</span>}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-emerald-400">{m.mastered_count}</span>
                        {m.mastered_rate != null && <span className="text-xs text-muted-foreground ml-1">({m.mastered_rate}%)</span>}
                      </TableCell>
                      <TableCell><span className="text-sm text-blue-400">{m.developing_count}</span></TableCell>
                      <TableCell>
                        {m.needs_work_count > 0 ? <span className="text-sm text-red-400 font-semibold">{m.needs_work_count}</span> : <span className="text-xs text-muted-foreground">0</span>}
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs ${reliabilityColors[m.reliability] || 'text-muted-foreground'}`}>
                          {reliabilityLabels[m.reliability] || m.reliability}
                        </span>
                        <span className="text-xs text-muted-foreground ml-1">({m.question_count}q/{m.quiz_count}qcm)</span>
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${decisionColors[m.suggested_decision] || ''}`}>
                          {decisionLabels[m.suggested_decision] || m.suggested_decision}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
                <p className="text-muted-foreground">Aucune notion taguée sur les questions.</p>
                <p className="text-xs text-muted-foreground mt-1">Associez des notions aux questions de vos QCM pour activer l'analyse par notion.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      </>)}
    </div>
  );
}
